import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import { Message } from "semantic-ui-react";
import Link from 'next/link';

const IcelandArticle = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/beaten-track-iceland`;

  return (
    <MainLayout>
      <NextSeo
        title='8 Off-The-Beaten-Track Places To Visit In Iceland | DAYMADE'
        description='The Blue Lagoon is nice but a trip to Iceland deserves peace and quiet to take in all the breathtaking nature. Here are the 8 must visits for Iceland off the beaten track.'
        openGraph={{
          title: '8 Off-The-Beaten-Track Places To Visit In Iceland | DAYMADE',
          description: 'The Blue Lagoon is nice but a trip to Iceland deserves peace and quiet to take in all the breathtaking nature. Here are the 8 must visits for Iceland off the beaten track.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/8-off-the-beaten-track-places-to-visit-in-iceland',
          images: [{
            url: `${IMAGE_DIRECTORY}/hero.jpg`,
            alt: 'woman standing at top of hill overlooking the mountainous Icelandic landscape'
          }]
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'northern lights,instagram,instagrammable,iceland,snowy,competition,donkey,travel,destinations,holiday,prizes,getaway,thermal,fjords'
        // }]}
      />
      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText='woman standing at top of hill overlooking the mountainous Icelandic landscape'
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
            8 Off-The-Beaten-Track Places To Visit <strong>In Iceland</strong>
          </h1>

          <div className='separator' style={{ width: '80%', marginTop: 10 }} />

          <p>
            <span className='bigFirstLetter'>I</span>celand’s rugged landscape has placed it on the bucket list of many a traveller, a fact that should be reason enough to cross it off your own as soon as possible! Despite the boost in tourism on this pristine Nordic island, the country itself is still quite off the beaten track. But where can you go when you want to escape the tourists who do make it to the Land of Fire and Ice? DAYMADE has put together a list of eight of our favourite non-touristy destinations to visit while in Iceland to fulfil your wanderlust and go where fewer tourists date to go, all without sacrificing the wow-factor of this gorgeous island.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt='several long-haired, white and brown donkeys on snowy plane'
            className='singleBlogImage'
          />

          <h4>
            1. Exploring Iceland on horseback
          </h4>

          <p>
            Iceland is known to be pricey and many people try to keep the travel budget in check while visiting by camping and packing some food from home. Both are excellent ideas, but one item DAYMADE would suggest splurging on is a rental car so you can reach all the quiet corners of the island, even if the roads are known to be a little bumpy!
          </p>

          <p>
            Beyond equipping yourself with a set of wheels, traversing the harsh Icelandic landscapes on horseback is actually our preferred way to see the country. The Icelandic horse has been bred in isolation for more than a millennium and descends from the horses the Vikings brought over from Scandinavia – a sight that’s become an integral part of Iceland’s landscape. Why not gallop through the countryside on the back of this unique breed and experience what it’s like to travel like a Viking?
          </p>

          <h4>
            2. Take a dip in the geothermal springs
          </h4>

          <p>
            One of Iceland’s top attractions is the volcanoes with volcanic eruptions happening around every 4-5 years. The geothermal heat generated by the volcanoes has created many other amazing attractions, such as natural hot pools and geothermal hot springs. Loads of these lush hot springs are sprinkled throughout the entire country so you can literally open a map and take your pick depending on what area you find yourself in. The geothermal activity is the reason Iceland exists in the first place and one of the best reasons to visit. Take a trip to the hot springs (and glaciers!) to find out the true meaning of why this country is known for its fire and ice.
          </p>

          <h4>
            3. Discover the natural beauty in Westfjords
          </h4>

          <p>
            Deserving of its name as “Iceland’s Best Kept Secret”, the Westfjords are a destination for all travellers looking for the real Icelandic experience. With 22,000 square kilometres of land populated by only 10,000 inhabitants, many tourists tend to skip this sparsely inhabited peninsula. There are so many highlights on this rural section of land ranging from Dynjandi Falls to Látrabjarg and all the way to the red beach of Rauðasandur Beach, to name just a few. The Westfjords’ unpaved roads, mountainous landscape and jagged coastline make it slightly difficult to get around by land. But if you decide to venture over to the Northwestern part of the country, you will be rewarded by unspoilt nature and the vast landscapes dotted by waterfalls, fjords and cliffs are sure to make for an incredible adventure.
          </p>

          <h4>
            4. Visit the fjords
          </h4>

          <p>
            Did you know that Iceland has 109 fjords? It’s virtually impossible to visit the country without seeing at least one. Iceland’s fjords offer loads of outdoor activities such as hiking, kayaking, whale watching, horseback riding and much more. Getting off the beaten path is well worth it as you’re sure to discover magical fjords that will astonish even the most well-seasoned travellers who thought they had seen it all. Adventuring around the fjords (with so many, you have plenty to choose from!) is an authentic way to travel like a local and will most definitely be a highlight of your trip.
          </p>

          <h4>
            5. Scuba dive in Silfra
          </h4>

          <p>
            Yes, you read that right! Iceland’s Silfra fissure is one of the world’s top diving sites for two main reasons. Firstly, the fissure is a crack between the Eurasian and North American continents and is the only place in the world where you can dive or snorkel directly in the space between two continental plates (these plates drift apart about 2 cm per year). Secondly, the water is some of the clearest on earth and the visibility in the Silfra fissure is over 100 metres. The water is a cold 2-4 degrees Celsius year-round (surprise!) since the water is glacial water streaming out of Langjökull (Iceland’s second largest glacier, only behind Vatnajökull). If all that diving has made you thirsty, take a sip directly from the water – it’s pure enough to drink!
          </p>

          <div className='doubleImageContainer'>
            <img
              src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
              alt='the Northern Lights glowing a blue/green color above snow covered rocks and reflecting off the cold waters'
              className='doubleBlogImage'
            />

            <img
              src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
              alt='straight road leads towards steep cliff-face through orange grassland, with a orange/pink sunset in the distance'
              className='doubleBlogImage'
            />
          </div>

          <h4>
            6. The Cliffs of Vestrahorn
          </h4>

          <p>
            Often referred to as “Batman Mountain”, Vestrahorn offers a welcome respite from the ever-increasing tourist hordes coming over to the island. Only a five minutes’ drive away from Höfn, Vestrahorn is easily accessible by car and offers amazing views of Iceland’s dramatic landscape. Extremely steep cliffs and craggy mountainside meet a flat black sand beach – perhaps the perfect location for seeing the green and blue ribbons of light painting the sky during a colourful Northern Lights show? On your way out you’re bound to see the beautiful and unique breed of Icelandic horses on the side of the road as well. Does it get any more serene than that?
          </p>

          <h4>
            7. Black Sand Beaches
          </h4>

          <p>
            If you’ve accepted the DAYMADE challenge to see as many black sand beaches around the world as possible (check out the black sand beaches in Bali), Iceland has more beaches for you to check off the list! The small fishing village of Vík í Mýrdal in the south of Iceland is a great starting-off point for visiting the country’s gorgeous black sand beaches. Reynisfjara features stretches of beach, basalt stacks (which you may recognise from Season 7 of Game of Thrones) and plenty of waves. Without any significant landmasses between Antarctica and the shores of Reynisfjara, waves have thousands of kilometres to build. This momentum makes these “sneaker waves” somewhat dangerous because their strength can literally sneak up on unsuspecting visitors and drag them out to sea – so be careful! Sólheimasandur is another gorgeous beach not too far from Vik, known for the Sólheimasandur Plane Wreck, where the white plane creates a stark contrast to the beach’s black sand. Iceland’s beaches aren’t tropical, but the raw untouched element is so uniquely Iceland that you’ll feel like you have been transported back to a different time.
          </p>

          <h4>
            8. Icelandic Music Rocks
          </h4>

          <p>
            It’s not only the landscape that draws people to Iceland, the Icelandic culture is quirky and creative and nowhere is this more evident than in the music scene. Many people have heard of Björk, GusGus, the Sugarcubes, Sigur Rós, Emilíana Torrini and Of Monsters and Men (to name just a few), but there are loads of Icelandic musicians stepping into the spotlight all the time and are well worth seeking out. Your trip will most likely begin and end in Reykjavík, so don’t leave without keeping an eye on the local venues to catch a live gig. You never know, you might be one of the first to discover the next big act coming from Iceland!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt='snow-covered icelandic mountains touching clouds and orange sunset'
            className='singleBlogImage'
          />

          <h4>
            Go North!
          </h4>

          <p>
            Iceland is truly the perfect place to go off the beaten path and explore some of the world’s most untouched and dramatic naturescapes. Let these eight tips by DAYMADE guide you during your next visit to Iceland so you can avoid the crowds and discover sides of the country only the locals dare to explore!
          </p>

          <Message>
            Always wanted to explore Iceland yourself?! Enter the {''}
            <Link href='/' style={{ margin: 0, fontWeight: 'bold' }}>
              DAYMADE Draw
            </Link>
            {''} for a chance to win an epic trip to Iceland!
          </Message>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default IcelandArticle;
